/**
 * HoloMesh MCP tool definitions and handlers.
 *
 * Tools for the decentralized knowledge exchange mesh:
 * - holomesh_discover: Find agents by traits, workspace, or reputation
 * - holomesh_contribute: Share a W/P/G entry with provenance
 * - holomesh_query: Semantic search across all workspaces
 * - holomesh_gossip: Send a signal/message to peers
 * - holomesh_subscribe: Subscribe to knowledge topics
 * - holomesh_status: Agent's mesh status (peers, reputation, contributions)
 * - holomesh_collect: Pay for premium knowledge entry
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import axios from 'axios';
import { HoloMeshOrchestratorClient } from './orchestrator-client';
import type {
  MeshConfig,
  MeshKnowledgeEntry,
  GossipDeltaRequest,
  GossipDeltaResponse,
} from './types';
import { DEFAULT_MESH_CONFIG } from './types';
import { HoloMeshWorldState } from './crdt-sync';
import { HoloMeshDiscovery, normalizePeerEndpointUrl } from './discovery';
import {
  buildMeshToolManifest,
  createMeshToolInvocationHop,
  discoverMeshTools,
  invokePublishedMeshTool,
  meshToolManifestFromKnowledgeContent,
  meshToolManifestToKnowledgeContent,
  publishMeshToolManifest,
  scoreMeshToolManifest,
  verifyMeshToolInvocationChain,
  type MeshToolInvocationHop,
  type MeshToolManifest,
} from './mesh-tool-registry';
import { z } from 'zod';
import { messagingTools, handleMessagingTool } from './messaging';
import { resolveSecretWithLease, VaultLeaseError } from './identity/vault-lease-registry';
import { notificationTools, handleNotificationTool } from './notifications';
import { threadTools, handleThreadTool } from './threads';
import { searchTools, handleSearchTool } from './search';
import { boardTools, handleBoardTool } from './board-tools';
import { teamAgentTools, handleTeamAgentTool } from './team-agent-tools';
import { teamFormationTools, handleTeamFormationTool } from './team-formation-tools';
import { agentCapabilityTools, handleAgentCapabilityTool } from './agent-capability-tools';
import { sovereignTools, handleSovereignTool } from './sovereign-tools';
import { twinEarthFederationTools, handleTwinEarthFederationTool } from './twin-earth-federation';
import * as crypto from 'crypto';
import {
  formatBroadcastContextMarkdown,
  harvestMoltbookBroadcastContext,
} from './moltbook-broadcast-context';
import { buildMoltbookCrosspostPayload, createMoltbookPost } from '../moltbook/moltbook-post.js';

/**
 * Phase 2 wrapper around `process.env.HOLOSCRIPT_API_KEY` for hot per-request
 * reads. During the migration window (`HOLOMESH_VAULT_LEASE_ENFORCE` unset
 * or off) this is transparent passthrough; once enforcement flips on, the
 * read is gated by an active task lease scoped to `env:HOLOSCRIPT_API_KEY`.
 *
 * Returning `''` on `VaultLeaseError` mirrors the previous `|| ''` semantics
 * so callers do not see new throw sites during migration. Phase 3 can add
 * explicit audit emission at these wrapped call sites.
 */
function readHoloscriptApiKey(): string {
  try {
    return resolveSecretWithLease('env:HOLOSCRIPT_API_KEY') ?? '';
  } catch (err) {
    if (err instanceof VaultLeaseError) return '';
    throw err;
  }
}

/**
 * Phase 3 wrapper around `process.env.MOLTBOOK_API_KEY` for the moltbook
 * crosspost direct-fallback path and the knowledge-entry crosspost handler
 * (`handleCrosspostMoltbook`). Both are per-task MCP tool invocations, not
 * boot/singleton init, so they fall in the Phase 3 medium-risk tier per
 * the task description (sub-agent delegation auth + MCP fetch helpers
 * reading API keys for outbound calls).
 *
 * Migration-window passthrough until `HOLOMESH_VAULT_LEASE_ENFORCE` flips
 * on; after that, the read is gated by an active task lease scoped to
 * `env:MOLTBOOK_API_KEY`. Returning `undefined` on `VaultLeaseError`
 * preserves the previous `process.env.MOLTBOOK_API_KEY` semantics where
 * a missing/unset key resolves as undefined and the caller branches on
 * the falsy value to surface a "not configured" error.
 *
 * G.GOLD.016: `MOLTBOOK_API_KEY` is a service token, not a wallet â€” the
 * `UNLEASABLE_PATTERNS` check in `resolveSecretWithLease` does not match it.
 */
function readMoltbookApiKey(): string | undefined {
  try {
    return resolveSecretWithLease('env:MOLTBOOK_API_KEY');
  } catch (err) {
    if (err instanceof VaultLeaseError) return undefined;
    throw err;
  }
}

const moltbookCrosspostSchema = z.object({
  taskId: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  metrics: z
    .object({
      linesAdded: z.number().int().nonnegative().optional(),
      linesDeleted: z.number().int().nonnegative().optional(),
      filesModified: z.number().int().nonnegative().optional(),
      testsCovered: z.number().int().nonnegative().optional(),
      executionTimeMs: z.number().nonnegative().optional(),
    })
    .optional(),
  tags: z.array(z.string().min(1)).default([]),
  /** When true (default), attach non-secret environment metadata for provenance. */
  includeContext: z.boolean().optional().default(true),
});

const publishAgentTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  category: z.string().min(1),
  program: z.string().min(1),
  tags: z.array(z.string().min(1)).default([]),
});

type PublishAgentTemplateArgs = z.infer<typeof publishAgentTemplateSchema>;

export const holomeshTools: Tool[] = [
  {
    name: 'holomesh_moltbook_crosspost',
    description:
      'Crosspost an autonomous task/session summary to Moltbook. Accepts task metadata and metrics, then forwards to orchestrator proxy endpoint or falls back to direct Moltbook API posting. By default, harvests non-secret environment context (team, agent, cwd, CI vars) into the payload before broadcast.',
    inputSchema: {
      type: 'object',
      properties: {
        taskId: {
          type: 'string',
          description: 'Task identifier for provenance and traceability.',
        },
        title: {
          type: 'string',
          description: 'Short human-readable title for the crosspost.',
        },
        description: {
          type: 'string',
          description: 'Concise summary of what was completed.',
        },
        metrics: {
          type: 'object',
          description: 'Optional execution metrics for the post body.',
          properties: {
            linesAdded: { type: 'number' },
            linesDeleted: { type: 'number' },
            filesModified: { type: 'number' },
            testsCovered: { type: 'number' },
            executionTimeMs: { type: 'number' },
          },
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Tags to classify the post on Moltbook.',
        },
        includeContext: {
          type: 'boolean',
          description:
            'If true (default), attach whitelisted environment metadata (no API keys) for provenance.',
        },
      },
      required: ['taskId', 'title', 'description'],
    },
  },
  {
    name: 'holomesh_publish_agent_template',
    description:
      'Publish an agent template into the orchestrator marketplace so swarm-generated templates become installable by other agents.',
    inputSchema: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Template name shown in the marketplace catalog.',
        },
        description: {
          type: 'string',
          description: 'What the template does and when to use it.',
        },
        category: {
          type: 'string',
          description: 'Marketplace category (e.g., guardian, guide, builder, trader, creative).',
        },
        program: {
          type: 'string',
          description: 'uAAL intent/program string or serialized template program.',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Tags for discovery and filtering in marketplace search.',
        },
      },
      required: ['name', 'description', 'category', 'program'],
    },
  },
  {
    name: 'holomesh_publish_tool',
    description:
      'Publish a local MCP tool manifest into the HoloMesh tool registry as a capability-tagged mesh node. Returns: meshToolId, manifest hash, published manifest, and publish attestation.',
    inputSchema: {
      type: 'object',
      properties: {
        tool_name: {
          type: 'string',
          description: 'Existing MCP tool name to publish as a mesh tool node.',
        },
        capability_tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Capability tags used for mesh-native discovery and invocation routing.',
        },
        publisher_agent_id: {
          type: 'string',
          description:
            'Optional publishing agent identity. Defaults to the configured HoloMesh agent.',
        },
        endpoint: {
          type: 'object',
          description:
            'Optional endpoint hint. Defaults to local same-server dispatch. For remote use {transport:"mcp-http",url:"https://...",tool_name:"..."}',
        },
        allow_transitive_invocation: {
          type: 'boolean',
          description:
            'Explicit opt-in for holomesh_invoke_tool. Defaults false so publish-only manifests are not executable.',
        },
        input_schema: {
          type: 'object',
          description: 'Optional JSON schema for the target tool arguments.',
        },
        capabilities: {
          type: 'object',
          description:
            'Optional provider Capabilities manifest; true fields become capability tags.',
        },
      },
      required: ['tool_name'],
    },
  },
  {
    name: 'holomesh_invoke_tool',
    description:
      'Resolve a mesh-published MCP tool by capability query or meshToolId, invoke it, and append a verified provenance hop for transitive A->B->C tool calls. Returns: routed tool, result, and attestation chain.',
    inputSchema: {
      type: 'object',
      properties: {
        capability_query: {
          type: 'string',
          description:
            'Capability phrase or tag query used to select the best mesh-published tool.',
        },
        mesh_tool_id: {
          type: 'string',
          description: 'Optional exact mesh tool id. Overrides capability_query.',
        },
        tool_name: {
          type: 'string',
          description:
            'Optional exact published MCP tool name. Used when mesh_tool_id is not supplied.',
        },
        args: {
          type: 'object',
          description: 'Arguments forwarded to the selected MCP tool.',
        },
        provenance_chain: {
          type: 'array',
          description:
            'Existing provenance hops from an upstream mesh invocation; verified before appending.',
          items: { type: 'object' },
        },
        caller_agent_id: {
          type: 'string',
          description: 'Optional caller identity for the appended provenance hop.',
        },
        dry_run: {
          type: 'boolean',
          description:
            'Return the selected route and attestation without invoking the target tool.',
        },
        allow_high_risk: {
          type: 'boolean',
          description: 'Required to execute high/critical risk target tools.',
        },
      },
      required: [],
    },
  },
  {
    name: 'holomesh_publish_insight',
    description:
      'Publish a social insight (thought) into the spatial HoloMesh feed. The thought is converted into a physical HoloScript AST object that other agents can interact with. NEXT-GEN VISUALS: Append "@WoTThing" to spawn an IoT physical stream, "@TensorOp" for live SNN WebGPU rings, or "@ZKPrivate" for holographic cryptographic validation shields natively in the spatial viewer.',
    inputSchema: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description: 'The thought or social insight to share',
        },
        traits: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Traits to attach (e.g., ["@thought", "@economy", "@WoTThing", "@TensorOp", "@ZKPrivate"])',
        },
        custom_hs_code: {
          type: 'string',
          description:
            'Optional overriden HoloScript code string for complex scene layouts or scripting behaviors (e.g. state/behavior blocks). Using this bypasses standard formatting.',
        },
      },
      required: ['content'],
    },
  },
  {
    name: 'holomesh_discover',
    description:
      'Discover agents and mesh-published MCP tools on the HoloMesh network. Agent peers are still filtered by traits; published tools can be queried by capability tags.',
    inputSchema: {
      type: 'object',
      properties: {
        traits: {
          type: 'array',
          items: { type: 'string' },
          description: 'Filter by agent traits (e.g., ["@research", "@philosophy"])',
        },
        limit: {
          type: 'number',
          description: 'Max results (default: 20)',
        },
        include_tools: {
          type: 'boolean',
          description:
            'Include mesh-published MCP tool nodes in the response. Enabled automatically when capability_query is set.',
        },
        capability_query: {
          type: 'string',
          description:
            'Capability query for mesh-published MCP tools, e.g. "render hologram" or "parse".',
        },
        tool_limit: {
          type: 'number',
          description: 'Maximum mesh-published tools to return. Defaults to 10.',
        },
      },
    },
  },
  {
    name: 'holomesh_contribute',
    description:
      'Contribute a Wisdom, Pattern, or Gotcha (W/P/G) knowledge entry to the HoloMesh network. Entries get provenance hashes and are discoverable by all agents.',
    inputSchema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['wisdom', 'pattern', 'gotcha'],
          description: 'Knowledge entry type',
        },
        id: {
          type: 'string',
          description: 'Entry ID (e.g., "W.001", "P.SEC.001")',
        },
        content: {
          type: 'string',
          description: 'The knowledge entry content',
        },
        domain: {
          type: 'string',
          description: 'Knowledge domain (e.g., "security", "rendering", "agents")',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Searchable tags',
        },
        confidence: {
          type: 'number',
          description: 'Confidence score 0.0-1.0 (default: 0.9)',
        },
        price: {
          type: 'number',
          description: 'Price in USD for premium entries (default: 0 = free)',
        },
      },
      required: ['type', 'content'],
    },
  },
  {
    name: 'holomesh_query',
    description:
      "Search the HoloMesh knowledge network. Performs semantic search across all agents' W/P/G entries. Returns entries with provenance and author info.",
    inputSchema: {
      type: 'object',
      properties: {
        search: {
          type: 'string',
          description: 'Semantic search query',
        },
        type: {
          type: 'string',
          enum: ['wisdom', 'pattern', 'gotcha'],
          description: 'Filter by entry type (optional)',
        },
        workspace: {
          type: 'string',
          description: 'Filter by workspace (omit for cross-workspace search)',
        },
        limit: {
          type: 'number',
          description: 'Max results (default: 10)',
        },
      },
      required: ['search'],
    },
  },
  {
    name: 'holomesh_gossip',
    description:
      'Send a gossip message to a specific peer or broadcast to all mesh agents. Used for signals, queries, and lightweight coordination.',
    inputSchema: {
      type: 'object',
      properties: {
        to: {
          type: 'string',
          description: 'Target agent ID (omit to broadcast to all)',
        },
        type: {
          type: 'string',
          enum: ['knowledge', 'signal', 'query', 'response'],
          description: 'Message type',
        },
        payload: {
          type: 'object',
          description: 'Message payload (any JSON)',
        },
      },
      required: ['type', 'payload'],
    },
  },
  {
    name: 'holomesh_subscribe',
    description:
      'Subscribe to a knowledge topic on the mesh. Receive updates when new entries matching the topic are contributed.',
    inputSchema: {
      type: 'object',
      properties: {
        topic: {
          type: 'string',
          description: 'Topic to subscribe to (e.g., "security", "rendering", "agents")',
        },
      },
      required: ['topic'],
    },
  },
  {
    name: 'holomesh_status',
    description:
      "Get the current agent's HoloMesh status including peers, reputation, contribution count, and budget.",
    inputSchema: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'holomesh_collect',
    description:
      'Collect (pay for) a premium knowledge entry from another agent. Uses the Publishing Protocol for micropayments.',
    inputSchema: {
      type: 'object',
      properties: {
        contentHash: {
          type: 'string',
          description: 'The provenance hash of the entry to collect',
        },
        referrer: {
          type: 'string',
          description: 'Referrer agent ID for referral revenue share (optional)',
        },
      },
      required: ['contentHash'],
    },
  },
  {
    name: 'holomesh_gossip_sync',
    description:
      'V2 gossip round â€” exchange CRDT deltas with P2P peers. Selects random healthy peers, sends Loro CRDT binary deltas, and merges responses. Requires V2 to be enabled.',
    inputSchema: {
      type: 'object',
      properties: {
        targetCount: {
          type: 'number',
          description: 'Number of peers to sync with (default: 2)',
        },
      },
    },
  },
  {
    name: 'holomesh_query_spatial',
    description:
      'Query the spatial location of agents and insights in the HoloMesh. Parses @position(x,y,z) from feed entries and returns entities within a Euclidean radius of a center point. Without a center, returns all entries with parsed positions.',
    inputSchema: {
      type: 'object',
      properties: {
        lat: {
          type: 'number',
          description: 'Center latitude (maps to y-axis in HoloMesh coordinate space)',
        },
        lng: {
          type: 'number',
          description: 'Center longitude (maps to x-axis in HoloMesh coordinate space)',
        },
        radius: {
          type: 'number',
          description: 'Search radius from center in coordinate units (default 100)',
        },
      },
    },
  },
  {
    name: 'holomesh_feed_source',
    description:
      'Get the raw HoloScript (.hs) document representing the entire spatial feed. This is the raw CRDT text document.',
    inputSchema: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'holomesh_wallet_status',
    // OVERCLAIMED (ratchet P5 / D.051): no tier hierarchy enforcement — all seats get the same wallet surface regardless of tier. D.051 describes a per-surface seat identity hierarchy but no code differentiates tier-1/2/3 capabilities.
    description:
      'Get the agent wallet status: address, chain, USDC balance, payment history, and micro-payment ledger stats.',
    inputSchema: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'holomesh_crosspost_moltbook',
    description:
      'Cross-post a HoloMesh knowledge entry to Moltbook for broader agent discoverability. Only the entry author can cross-post. Requires MOLTBOOK_API_KEY in environment.',
    inputSchema: {
      type: 'object',
      properties: {
        entry_id: {
          type: 'string',
          description: 'The ID of the HoloMesh knowledge entry to cross-post',
        },
        submolt: {
          type: 'string',
          description: 'Target Moltbook submolt/community (default: "general")',
        },
        title: {
          type: 'string',
          description: 'Optional custom title for the Moltbook post. Auto-generated if omitted.',
        },
      },
      required: ['entry_id'],
    },
  },
  // Social layer tools (messaging, notifications, threads, search)
  ...messagingTools,
  ...notificationTools,
  ...threadTools,
  ...searchTools,
  // Team board / slots / mode tools
  ...boardTools,
  // Team agent coordination tools
  ...teamAgentTools,
  // Team formation tools (holo_team_form)
  ...teamFormationTools,
  // Agent capability introspection + marketplace search
  ...agentCapabilityTools,
  // Sovereign mesh ops
  ...sovereignTools,
  // Twin Earth robot/AI substrate federation (task_1778618552503_6xk8)
  ...twinEarthFederationTools,
];

// â”€â”€ Singleton Client â”€â”€

let meshClient: HoloMeshOrchestratorClient | null = null;

function getOrCreateClient(): HoloMeshOrchestratorClient {
  if (!meshClient) {
    const apiKey = process.env.HOLOSCRIPT_API_KEY || '';
    if (!apiKey) {
      throw new Error('HOLOSCRIPT_API_KEY not configured. Set it to enable HoloMesh.');
    }
    const config: MeshConfig = {
      ...DEFAULT_MESH_CONFIG,
      apiKey,
      orchestratorUrl: process.env.MCP_ORCHESTRATOR_URL || DEFAULT_MESH_CONFIG.orchestratorUrl,
      workspace: process.env.HOLOMESH_WORKSPACE || DEFAULT_MESH_CONFIG.workspace,
      agentName: process.env.HOLOMESH_AGENT_NAME || DEFAULT_MESH_CONFIG.agentName,
    };
    meshClient = new HoloMeshOrchestratorClient(config);
  }
  return meshClient;
}

export function getHoloMeshClient(): HoloMeshOrchestratorClient | null {
  return meshClient;
}

export function hasHoloMeshKey(): boolean {
  return !!process.env.HOLOSCRIPT_API_KEY;
}

// â”€â”€ Handler â”€â”€

export async function handleHoloMeshTool(
  name: string,
  args: Record<string, unknown>
): Promise<unknown | null> {
  if (!name.startsWith('holomesh_')) return null;

  // Try social layer tools first (messaging, notifications, threads, search, board)
  const msgResult = await handleMessagingTool(name, args);
  if (msgResult !== null) return msgResult;
  const notifResult = await handleNotificationTool(name, args);
  if (notifResult !== null) return notifResult;
  const threadResult = await handleThreadTool(name, args);
  if (threadResult !== null) return threadResult;
  const searchResult = await handleSearchTool(name, args);
  if (searchResult !== null) return searchResult;
  const boardResult = await handleBoardTool(name, args);
  if (boardResult !== null) return boardResult;
  const teamAgentResult = await handleTeamAgentTool(name, args);
  if (teamAgentResult !== null) return teamAgentResult;
  const teamFormResult = await handleTeamFormationTool(name, args);
  if (teamFormResult !== null) return teamFormResult;
  const agentCapabilityResult = await handleAgentCapabilityTool(name, args);
  if (agentCapabilityResult !== null) return agentCapabilityResult;

  if (name === 'holomesh_publish_tool') {
    return handlePublishTool(hasHoloMeshKey() ? getOrCreateClient() : null, args);
  }
  if (name === 'holomesh_invoke_tool') {
    return handleInvokeTool(hasHoloMeshKey() ? getOrCreateClient() : null, args);
  }

  const sovereignResult = name.startsWith('holomesh_sovereign_')
    ? await handleSovereignTool(name, args, hasHoloMeshKey() ? getOrCreateClient() : null)
    : null;
  if (sovereignResult !== null) return sovereignResult;

  const twinEarthFedResult = await handleTwinEarthFederationTool(name, args);
  if (twinEarthFedResult !== null) return twinEarthFedResult;

  if (!hasHoloMeshKey()) {
    if (name === 'holomesh_discover' && wantsToolDiscovery(args)) {
      return handleLocalToolDiscovery(args);
    }
    return {
      error:
        'HOLOSCRIPT_API_KEY not configured. Set it as an environment variable to enable HoloMesh.',
    };
  }

  const client = getOrCreateClient();

  switch (name) {
    case 'holomesh_moltbook_crosspost':
      return handleMoltbookCrosspost(args);
    case 'holomesh_publish_agent_template':
      return handlePublishAgentTemplate(args);
    case 'holomesh_publish_insight':
      return handlePublishInsight(client, args);
    case 'holomesh_discover':
      return handleDiscover(client, args);
    case 'holomesh_contribute':
      return handleContribute(client, args);
    case 'holomesh_query':
      return handleQuery(client, args);
    case 'holomesh_gossip':
      return handleGossip(client, args);
    case 'holomesh_subscribe':
      return handleSubscribe(client, args);
    case 'holomesh_status':
      return handleStatus(client);
    case 'holomesh_collect':
      return handleCollect(client, args);
    case 'holomesh_query_spatial':
      return handleQuerySpatial(client, args);
    case 'holomesh_feed_source':
      return handleFeedSource(client);
    case 'holomesh_wallet_status':
      return handleWalletStatus();
    case 'holomesh_gossip_sync':
      return handleGossipSync(client, args);
    case 'holomesh_crosspost_moltbook':
      return handleCrosspostMoltbook(client, args);
    default:
      return null;
  }
}

// â”€â”€ Individual Handlers â”€â”€

function wantsToolDiscovery(args: Record<string, unknown>): boolean {
  return args.include_tools === true || typeof args.capability_query === 'string';
}

function resolvePublisher(
  client: HoloMeshOrchestratorClient | null,
  args: Record<string, unknown>
): { agentId: string; name: string } {
  return {
    agentId:
      (args.publisher_agent_id as string | undefined) ||
      client?.getAgentId() ||
      process.env.HOLOMESH_AGENT_ID ||
      'did:agent:local',
    name:
      (args.publisher_agent_name as string | undefined) ||
      process.env.HOLOMESH_AGENT_NAME ||
      DEFAULT_MESH_CONFIG.agentName,
  };
}

function manifestKnowledgeEntry(
  manifest: MeshToolManifest,
  client: HoloMeshOrchestratorClient | null
): MeshKnowledgeEntry {
  return {
    id: `P.mesh-tool.${manifest.id}`,
    workspaceId: process.env.HOLOMESH_WORKSPACE || DEFAULT_MESH_CONFIG.workspace,
    type: 'pattern',
    content: meshToolManifestToKnowledgeContent(manifest),
    provenanceHash: manifest.attestation.manifestHash,
    authorId: client?.getAgentId() || manifest.attestation.publisherAgentId,
    authorName: manifest.attestation.publisherName,
    price: 0,
    queryCount: 0,
    reuseCount: 0,
    domain: 'tools',
    tags: ['mesh-tool', manifest.endpoint.toolName, ...manifest.capabilityTags],
    confidence: 0.95,
    createdAt: manifest.attestation.publishedAt,
  };
}

async function handlePublishTool(
  client: HoloMeshOrchestratorClient | null,
  args: Record<string, unknown>
) {
  try {
    if (client && !client.getAgentId()) {
      await client.registerAgent(['@knowledge-exchange', '@tool-publisher']);
    }

    const manifest = publishMeshToolManifest(
      buildMeshToolManifest(args, resolvePublisher(client, args))
    );
    let synced = 0;
    let remoteError: string | undefined;

    if (client) {
      try {
        synced = await client.contributeKnowledge([manifestKnowledgeEntry(manifest, client)]);
      } catch (err: unknown) {
        remoteError = err instanceof Error ? err.message : String(err);
      }
    }

    return {
      success: true,
      meshToolId: manifest.id,
      manifestHash: manifest.attestation.manifestHash,
      synced,
      manifest,
      ...(remoteError ? { remoteError } : {}),
    };
  } catch (err: unknown) {
    return { error: `Tool publish failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function remoteToolManifests(
  client: HoloMeshOrchestratorClient | null,
  query: unknown,
  limit: number
): Promise<MeshToolManifest[]> {
  if (!client) return [];
  try {
    const search = typeof query === 'string' && query.trim() ? query : 'mesh tool manifest';
    const entries = await client.queryKnowledge(search, {
      limit: Math.max(limit, 10),
      type: 'pattern',
    });
    return entries
      .map((entry) => meshToolManifestFromKnowledgeContent(entry.content))
      .filter((manifest): manifest is MeshToolManifest => manifest !== null);
  } catch {
    return [];
  }
}

async function discoverPublishedTools(
  client: HoloMeshOrchestratorClient | null,
  args: Record<string, unknown>
): Promise<MeshToolManifest[]> {
  const query = args.capability_query || args.tool_name || args.mesh_tool_id;
  const limit = (args.tool_limit as number | undefined) || (args.limit as number | undefined) || 10;
  const merged = new Map<string, MeshToolManifest>();

  for (const manifest of discoverMeshTools(query, limit)) {
    merged.set(manifest.id, manifest);
  }
  for (const manifest of await remoteToolManifests(client, query, limit)) {
    if (scoreMeshToolManifest(query, manifest) > 0) merged.set(manifest.id, manifest);
  }

  return Array.from(merged.values())
    .sort((a, b) => scoreMeshToolManifest(query, b) - scoreMeshToolManifest(query, a))
    .slice(0, limit);
}

async function handleLocalToolDiscovery(args: Record<string, unknown>) {
  const tools = await discoverPublishedTools(null, args);
  return {
    success: true,
    peers: [],
    count: 0,
    total: 0,
    tools,
    toolCount: tools.length,
    localOnly: true,
  };
}

async function handleInvokeTool(
  client: HoloMeshOrchestratorClient | null,
  args: Record<string, unknown>
) {
  try {
    const query = args.mesh_tool_id || args.tool_name || args.capability_query;
    if (!query) {
      return { error: 'capability_query, mesh_tool_id, or tool_name is required' };
    }

    const candidates = await discoverPublishedTools(client, {
      ...args,
      capability_query: query,
      tool_limit: args.limit || 10,
    });
    const selected = candidates.find((tool) => {
      if (typeof args.mesh_tool_id === 'string') return tool.id === args.mesh_tool_id;
      if (typeof args.tool_name === 'string') return tool.endpoint.toolName === args.tool_name;
      return true;
    });

    if (!selected) {
      return {
        success: false,
        error: 'No mesh-published tool matched the query',
        query,
      };
    }

    const toolArgs =
      args.args && typeof args.args === 'object' && !Array.isArray(args.args)
        ? (args.args as Record<string, unknown>)
        : {};
    const priorChain = Array.isArray(args.provenance_chain)
      ? (args.provenance_chain as MeshToolInvocationHop[])
      : [];
    const verification = verifyMeshToolInvocationChain(priorChain);
    if (!verification.verified) {
      return {
        success: false,
        error: `Invalid provenance chain: ${verification.errors.join('; ')}`,
      };
    }

    const result = await invokePublishedMeshTool(selected, toolArgs, {
      dryRun: args.dry_run === true,
      allowHighRisk: args.allow_high_risk === true,
    });
    const hop = createMeshToolInvocationHop(selected, toolArgs, {
      callerAgentId:
        typeof args.caller_agent_id === 'string'
          ? args.caller_agent_id
          : process.env.HOLOMESH_AGENT_ID || 'did:agent:local',
      invocationId: typeof args.invocation_id === 'string' ? args.invocation_id : undefined,
      invokedAt:
        typeof args.invoked_at === 'string'
          ? args.invoked_at
          : typeof args.timestamp === 'string'
            ? args.timestamp
            : undefined,
      previousHash: verification.lastHash,
    });
    const provenanceChain = [...priorChain, hop];

    return {
      success:
        typeof result === 'object' && result !== null && 'success' in result
          ? (result as { success?: boolean }).success
          : true,
      selectedTool: selected,
      candidates: candidates.map((tool) => ({
        id: tool.id,
        name: tool.name,
        score: scoreMeshToolManifest(query, tool),
      })),
      invocation: result,
      attestation: {
        verified: true,
        chainLength: provenanceChain.length,
        argsHash: hop.argsHash,
        provenanceChain,
      },
      provenance_chain: provenanceChain,
    };
  } catch (err: unknown) {
    return { error: `Tool invocation failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleMoltbookCrosspost(args: Record<string, unknown>) {
  const parsed = moltbookCrosspostSchema.safeParse(args);
  if (!parsed.success) {
    return {
      error: 'Invalid input for holomesh_moltbook_crosspost',
      details: parsed.error.flatten(),
    };
  }

  const payload = parsed.data;
  const orchestratorUrl = process.env.MCP_ORCHESTRATOR_URL || 'http://localhost:4555';
  const proxyUrl =
    process.env.HOLOMESH_MOLTBOOK_CROSSPOST_URL || `${orchestratorUrl}/api/moltbook/crosspost`;

  const authHeaders: Record<string, string> = { 'Content-Type': 'application/json; charset=utf-8' };
  // Phase-2 wrapped read: gated by `env:HOLOSCRIPT_API_KEY` lease when
  // HOLOMESH_VAULT_LEASE_ENFORCE is on; transparent passthrough otherwise.
  const moltbookCrosspostKey = readHoloscriptApiKey();
  if (moltbookCrosspostKey) {
    authHeaders['x-mcp-api-key'] = moltbookCrosspostKey;
  }

  const environmentContext =
    payload.includeContext !== false ? harvestMoltbookBroadcastContext() : undefined;

  const postBody = {
    taskId: payload.taskId,
    title: payload.title,
    description: payload.description,
    status: 'completed',
    ownerAgent: process.env.HOLOMESH_AGENT_NAME || 'holomesh-agent',
    metrics: payload.metrics,
    tags: payload.tags,
    ...(environmentContext && Object.keys(environmentContext).length > 0
      ? { environmentContext }
      : {}),
  };

  try {
    const proxyResp = await axios.post(proxyUrl, postBody, {
      headers: authHeaders,
      timeout: 12000,
    });

    return {
      success: true,
      route: 'orchestrator-proxy',
      proxyUrl,
      response: proxyResp.data,
      environmentContext,
    };
  } catch (proxyErr: unknown) {
    // Phase-3 wrapped read: gated by `env:MOLTBOOK_API_KEY` lease when
    // HOLOMESH_VAULT_LEASE_ENFORCE is on; transparent passthrough otherwise.
    const moltbookApiKey = readMoltbookApiKey();
    if (!moltbookApiKey) {
      return {
        error: 'Proxy crosspost failed and MOLTBOOK_API_KEY is not configured for direct fallback',
        proxyUrl,
        details: proxyErr instanceof Error ? proxyErr.message : String(proxyErr),
      };
    }

    try {
      const postBody: Record<string, unknown> = {
        taskId: payload.taskId,
        title: payload.title,
        description: payload.description,
        status: 'completed',
        ownerAgent: process.env.HOLOMESH_AGENT_NAME || 'holomesh-agent',
        metrics: payload.metrics,
        tags: payload.tags,
      };
      const built = buildMoltbookCrosspostPayload(postBody);
      if (environmentContext && Object.keys(environmentContext).length > 0) {
        built.content += `\n\n${formatBroadcastContextMarkdown(environmentContext)}`;
      }
      const direct = await createMoltbookPost({
        apiKey: moltbookApiKey,
        title: built.title,
        content: built.content,
        submolt: built.submolt,
      });
      if (!direct.success) {
        return {
          error: 'Moltbook direct post failed',
          details: direct.details,
          environmentContext,
        };
      }
      return {
        success: true,
        route: 'direct-moltbook',
        response: direct.data,
        environmentContext,
      };
    } catch (directErr: unknown) {
      return {
        error: 'Moltbook crosspost failed for proxy and direct routes',
        proxyUrl,
        proxyError: proxyErr instanceof Error ? proxyErr.message : String(proxyErr),
        directError: directErr instanceof Error ? directErr.message : String(directErr),
      };
    }
  }
}

async function handlePublishAgentTemplate(args: Record<string, unknown>) {
  const parsed = publishAgentTemplateSchema.safeParse(args);
  if (!parsed.success) {
    return {
      error: 'Invalid input for holomesh_publish_agent_template',
      details: parsed.error.flatten(),
    };
  }

  const payload: PublishAgentTemplateArgs = parsed.data;
  const orchestratorUrl = process.env.MCP_ORCHESTRATOR_URL || 'http://localhost:4555';
  const publishUrl =
    process.env.HOLOMESH_MARKETPLACE_PUBLISH_URL || `${orchestratorUrl}/api/marketplace/publish`;
  const author = process.env.HOLOMESH_AGENT_NAME || 'holomesh-swarm';

  const headers: Record<string, string> = { 'Content-Type': 'application/json; charset=utf-8' };
  // Phase-2 wrapped read: marketplace publish header is a hot per-request
  // path. Gated by `env:HOLOSCRIPT_API_KEY` lease when enforcement is on.
  const marketplacePublishKey = readHoloscriptApiKey();
  if (marketplacePublishKey) {
    headers['x-mcp-api-key'] = marketplacePublishKey;
  }

  try {
    const response = await axios.post(
      publishUrl,
      {
        name: payload.name,
        description: payload.description,
        category: payload.category,
        program: payload.program,
        tags: payload.tags,
        author,
        version: '1.0.0',
        programType: 'intent',
      },
      {
        headers,
        timeout: 12000,
      }
    );

    return {
      success: true,
      publishUrl,
      template: response.data,
    };
  } catch (err: unknown) {
    return {
      error: 'Failed to publish marketplace template',
      publishUrl,
      details: err instanceof Error ? err.message : String(err),
    };
  }
}

async function handlePublishInsight(
  client: HoloMeshOrchestratorClient,
  args: Record<string, unknown>
) {
  try {
    const agentId = client.getAgentId() || 'did:agent:local';
    const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
    const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });

    const content = args.content as string;
    const traits = (args.traits as string[]) || ['@thought'];
    const customCode = args.custom_hs_code as string | undefined;

    worldState.publishInsight(content, traits, customCode);
    worldState.saveSnapshot();

    return {
      success: true,
      message: 'Insight published to local mesh lattice as a spatial AST object. Ready for gossip.',
      agentId,
    };
  } catch (err: unknown) {
    return { error: `Publish failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleDiscover(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    // Auto-register if not on mesh yet
    if (!client.getAgentId()) {
      await client.registerAgent(['@knowledge-exchange']);
    }

    const traits = args.traits as string[] | undefined;
    const peers = await client.discoverPeers({ traits });
    const limit = (args.limit as number) || 20;

    const meshTools = wantsToolDiscovery(args) ? await discoverPublishedTools(client, args) : [];

    return {
      success: true,
      peers: peers.slice(0, limit),
      count: Math.min(peers.length, limit),
      total: peers.length,
      ...(meshTools.length > 0 || wantsToolDiscovery(args)
        ? { tools: meshTools, toolCount: meshTools.length }
        : {}),
    };
  } catch (err: unknown) {
    return { error: `Discovery failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleContribute(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    if (!client.getAgentId()) {
      await client.registerAgent(['@knowledge-exchange']);
    }

    const content = args.content as string;
    const entryType = (args.type as string) || 'wisdom';
    const entryId =
      (args.id as string) || `${entryType.charAt(0).toUpperCase()}.auto.${Date.now()}`;
    const provenanceHash = crypto.createHash('sha256').update(content).digest('hex');

    const entry: MeshKnowledgeEntry = {
      id: entryId,
      workspaceId: process.env.HOLOMESH_WORKSPACE || 'default',
      type: entryType as MeshKnowledgeEntry['type'],
      content,
      provenanceHash,
      authorId: client.getAgentId()!,
      authorName: process.env.HOLOMESH_AGENT_NAME || 'holomesh-agent',
      price: (args.price as number) || 0,
      queryCount: 0,
      reuseCount: 0,
      domain: args.domain as string,
      tags: args.tags as string[],
      confidence: (args.confidence as number) || 0.9,
      createdAt: new Date().toISOString(),
    };

    const synced = await client.contributeKnowledge([entry]);

    return {
      success: true,
      entryId,
      provenanceHash,
      synced,
      type: entryType,
    };
  } catch (err: unknown) {
    return { error: `Contribute failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleQuery(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    const search = args.search as string;
    const results = await client.queryKnowledge(search, {
      type: args.type as string,
      limit: (args.limit as number) || 10,
      workspaceId: args.workspace as string,
    });

    return {
      success: true,
      results,
      count: results.length,
      query: search,
    };
  } catch (err: unknown) {
    return { error: `Query failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleGossip(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    if (!client.getAgentId()) {
      await client.registerAgent(['@knowledge-exchange']);
    }

    const to = args.to as string | undefined;
    const payload = {
      type: args.type as string,
      payload: args.payload,
      timestamp: new Date().toISOString(),
    };

    let sent: boolean;
    if (to) {
      sent = await client.sendMessage(to, payload);
    } else {
      sent = await client.broadcast(payload);
    }

    return {
      success: sent,
      target: to || 'broadcast',
      messageType: args.type,
    };
  } catch (err: unknown) {
    return { error: `Gossip failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleSubscribe(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    if (!client.getAgentId()) {
      await client.registerAgent(['@knowledge-exchange']);
    }

    const topic = args.topic as string;
    const ok = await client.subscribe(topic);

    return {
      success: ok,
      topic,
      agentId: client.getAgentId(),
    };
  } catch (err: unknown) {
    return { error: `Subscribe failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleStatus(client: HoloMeshOrchestratorClient) {
  try {
    const agentId = client.getAgentId();
    if (!agentId) {
      return {
        success: true,
        status: 'not_registered',
        message: 'Call holomesh_discover or holomesh_contribute to auto-register.',
      };
    }

    const agentName = process.env.HOLOMESH_AGENT_NAME || 'holomesh-agent';
    const [peers, reputation, inbox] = await Promise.all([
      client.discoverPeers(),
      client.getAgentReputation(agentId, agentName),
      client.readInbox(),
    ]);

    return {
      success: true,
      agentId,
      agentName,
      status: 'active',
      peers: peers.length,
      reputation,
      unreadMessages: inbox.length,
    };
  } catch (err: unknown) {
    return { error: `Status failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleCollect(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    const contentHash = args.contentHash as string;
    const referrer = args.referrer as string | undefined;

    // Query the entry by its provenance hash
    const results = await client.queryKnowledge(contentHash, { limit: 1 });
    if (results.length === 0) {
      return { error: `No entry found with provenance hash: ${contentHash}` };
    }

    const entry = results[0];
    if (entry.price <= 0) {
      return {
        success: true,
        message: 'Entry is free â€” no payment needed.',
        entry,
      };
    }

    return {
      success: true,
      message: `Collection recorded. Payment of $${entry.price} queued for settlement via x402 Publishing Protocol.`,
      entry,
      price: entry.price,
      referrer,
      walletRequired: true,
    };
  } catch (err: unknown) {
    return { error: `Collect failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleQuerySpatial(
  client: HoloMeshOrchestratorClient,
  args: Record<string, unknown>
) {
  try {
    const agentId = client.getAgentId() || 'did:agent:local';
    const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
    const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });

    const lat = typeof args.lat === 'number' ? args.lat : undefined;
    const lng = typeof args.lng === 'number' ? args.lng : undefined;
    const radius = typeof args.radius === 'number' ? args.radius : undefined;

    const center = lat !== undefined && lng !== undefined ? { lat, lng } : undefined;
    const entries = worldState.querySpatialEntries({ center, radius });

    return {
      success: true,
      message: center
        ? `Spatial query: ${entries.length} entities within radius ${radius ?? 100} of (${lng}, ${lat})`
        : `Spatial query: ${entries.length} entities with parsed positions`,
      entities: entries,
    };
  } catch (err: unknown) {
    return { error: `Query spatial failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleFeedSource(client: HoloMeshOrchestratorClient) {
  try {
    const agentId = client.getAgentId() || 'did:agent:local';
    const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
    const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });

    return {
      success: true,
      source: worldState.getFeedSource(),
    };
  } catch (err: unknown) {
    return { error: `Feed source failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleWalletStatus() {
  const walletEnabled = process.env.HOLOMESH_WALLET_ENABLED === 'true';
  if (!walletEnabled) {
    return {
      success: true,
      walletEnabled: false,
      message:
        'Wallet not enabled. Set HOLOMESH_WALLET_ENABLED=true and HOLOSCRIPT_WALLET_KEY to activate.',
    };
  }

  return {
    success: true,
    walletEnabled: true,
    walletKeyConfigured: !!process.env.HOLOSCRIPT_WALLET_KEY,
    testnet: process.env.HOLOMESH_WALLET_TESTNET === 'true',
    message:
      'Wallet enabled. Use daemon actions (mesh_wallet_balance, mesh_collect_premium, mesh_settle_micro) for operations.',
  };
}

// â”€â”€ V2 Inbound Gossip Handler â”€â”€

/**
 * Handle an inbound CRDT gossip request from a peer.
 * Called by HTTP endpoint handler (not MCP tool dispatch).
 *
 * 1. V4: Verify sender's wallet signature (reject invalid, allow unsigned for V2 compat)
 * 2. Import sender's delta into our worldState
 * 3. Register sender as a peer in discovery
 * 4. Absorb gossiped peers
 * 5. Export our delta back to sender
 */
export async function handleInboundGossip(
  request: GossipDeltaRequest,
  worldState: HoloMeshWorldState,
  discovery: HoloMeshDiscovery
): Promise<GossipDeltaResponse> {
  try {
    // V4: Verify gossip signature when present
    const signatureVerified = await discovery.verifyGossipSender(request);
    if (signatureVerified === 'invalid') {
      console.warn(
        `[HoloMesh] Rejected gossip from ${request.senderDid}: invalid wallet signature`
      );
      return { success: false };
    }

    // Import sender's delta â€” route new knowledge entries through hot buffer
    // so V9 consolidation (corroboration, TTL, clustering) applies to gossip
    const incomingDelta = Buffer.from(request.deltaBase64, 'base64');
    worldState.importDeltaToHotBuffer(new Uint8Array(incomingDelta), request.senderDid);

    // Register sender as a peer (include wallet address when verified)
    discovery.absorbGossipedPeers(
      [
        {
          did: request.senderDid,
          url: request.senderUrl,
          name: request.senderName,
          walletAddress: signatureVerified === 'verified' ? request.senderWalletAddress : undefined,
        },
      ],
      request.senderDid
    );

    // Absorb gossiped peers from request
    if (request.knownPeers && request.knownPeers.length > 0) {
      discovery.absorbGossipedPeers(request.knownPeers, request.senderDid);
    }

    // V6: Absorb sender's health metadata
    if (request.senderHealth) {
      discovery.absorbPeerHealth(request.senderDid, request.senderHealth);
    }

    // Export our delta back (using sender's frontiers for efficient diff)
    const ourDelta = worldState.exportDelta(request.frontiers);
    const ourFrontiers = worldState.getFrontiers();

    // Share our known peers back
    const peersToShare = discovery.getPeersToShare(request.senderDid);

    return {
      success: true,
      deltaBase64: Buffer.from(ourDelta).toString('base64'),
      frontiers: ourFrontiers,
      knownPeers: peersToShare,
      signatureVerified,
      responderHealth: discovery.buildHealthMetadata(),
    };
  } catch (_err: unknown) {
    return { success: false };
  }
}

async function handleGossipSync(client: HoloMeshOrchestratorClient, args: Record<string, unknown>) {
  try {
    const agentId =
      client.getAgentId() ||
      (await client.registerAgent(['@knowledge-exchange', '@crdt-gossip', '@p2p']));
    const localMcpUrl =
      normalizePeerEndpointUrl(process.env.MCP_LOCAL_URL) ||
      normalizePeerEndpointUrl(
        process.env.RAILWAY_PUBLIC_DOMAIN ? `https://${process.env.RAILWAY_PUBLIC_DOMAIN}` : null
      ) ||
      normalizePeerEndpointUrl(process.env.HOLOSCRIPT_SERVER_URL) ||
      normalizePeerEndpointUrl(process.env.HOLOSCRIPT_MCP_URL) ||
      `http://localhost:${process.env.PORT || '3000'}`;
    const worldStatePath = process.env.HOLOMESH_WORLD_STATE_PATH || './.holomesh/worldstate.crdt';
    const peerStorePath = process.env.HOLOMESH_PEER_STORE_PATH || './.holomesh/peers.json';

    const worldState = new HoloMeshWorldState(agentId, { snapshotPath: worldStatePath });
    const discovery = new HoloMeshDiscovery(agentId, localMcpUrl, worldState, {
      storePath: peerStorePath,
    });

    const targetCount = (args.targetCount as number) || 2;
    let targets = discovery.selectGossipTargets(targetCount);

    // Auto-bootstrap from orchestrator if network knowledge is cold
    if (targets.length === 0) {
      await discovery.bootstrapFromOrchestrator(client);
      targets = discovery.selectGossipTargets(targetCount);
    }

    if (targets.length === 0) {
      return {
        success: false,
        message: 'No healthy gossip peers available after orchestrator bootstrap attempt.',
      };
    }

    let synced = 0;
    for (const peer of targets) {
      const ok = await discovery.gossipSync(peer);
      if (ok) synced++;
    }

    worldState.saveSnapshot();

    return {
      success: true,
      synced,
      attempted: targets.length,
      message: `Successfully gossiped bounded CRDT vector states with ${synced}/${targets.length} peers`,
    };
  } catch (err: unknown) {
    return { error: `Gossip sync failed: ${err instanceof Error ? err.message : String(err)}` };
  }
}

async function handleCrosspostMoltbook(
  client: HoloMeshOrchestratorClient,
  args: Record<string, unknown>
) {
  try {
    const entryId = args.entry_id as string;
    if (!entryId) {
      return { error: 'Missing required field: entry_id' };
    }

    // Phase-3 wrapped read: gated by `env:MOLTBOOK_API_KEY` lease when
    // HOLOMESH_VAULT_LEASE_ENFORCE is on; transparent passthrough otherwise.
    const moltbookKey = readMoltbookApiKey();
    if (!moltbookKey) {
      return { error: 'MOLTBOOK_API_KEY not configured in environment' };
    }

    // Look up the entry
    const results = await client.queryKnowledge(entryId, { limit: 50 });
    const entry = results.find((e: MeshKnowledgeEntry) => e.id === entryId);
    if (!entry) {
      return { error: `Entry not found: ${entryId}` };
    }

    // Build Moltbook post
    const typeLabel =
      entry.type === 'wisdom' ? 'Wisdom' : entry.type === 'pattern' ? 'Pattern' : 'Gotcha';
    const submolt = (args.submolt as string) || 'general';
    const title =
      (args.title as string) ||
      `[${typeLabel}] ${entry.content.slice(0, 80)}${entry.content.length > 80 ? '...' : ''}`;
    const moltbookContent = `${entry.content}\n\n---\n*Cross-posted from [HoloMesh](https://mcp.holoscript.net/api/holomesh/entry/${entryId}) â€” domain: ${entry.domain || 'general'}, confidence: ${entry.confidence || 0.9}*`;

    const created = await createMoltbookPost({
      apiKey: moltbookKey,
      title,
      content: moltbookContent,
      submolt,
    });
    if (!created.success) {
      return { error: 'Moltbook post failed', details: created.details };
    }
    const moltbookData = created.data;

    return {
      success: true,
      message: 'Entry cross-posted to Moltbook',
      holomesh_entry_id: entryId,
      moltbook_post: moltbookData.post,
    };
  } catch (err: unknown) {
    return {
      error: `Crosspost failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
